/**
 * ExtractedFieldsEditor — modal that lets the user tweak the fields Claude
 * Vision pulled from a document and lock the doc as "Confirmed" once every
 * value is validated.
 *
 * Fields render dynamically from `doc.attributes.extracted` (arbitrary shape
 * per category).  Numeric-looking values get a number input, everything
 * else falls back to text.  Booleans get a checkbox.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, ShieldCheck, Loader2, Sparkles } from "lucide-react";


const HIDDEN_KEYS = new Set(["raw_text", "confidence", "raw_text_preview"]);


function guessInputType(key, value) {
  if (typeof value === "boolean") return "checkbox";
  if (typeof value === "number") return "number";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return "date";
  return "text";
}


function humanizeKey(k) {
  return k.replace(/_/g, " ")
          .replace(/^./, (c) => c.toUpperCase());
}


export default function ExtractedFieldsEditor({ doc, onClose, onSaved }) {
  const initial = useMemo(() => {
    const src = doc?.attributes?.extracted || {};
    const clean = {};
    Object.entries(src).forEach(([k, v]) => {
      if (!HIDDEN_KEYS.has(k)) clean[k] = v;
    });
    return clean;
  }, [doc]);

  const [fields, setFields] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFields(initial); }, [initial]);

  if (!doc) return null;

  const setField = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));

  const addField = () => {
    const key = window.prompt("Nom du nouveau champ (ex : numero_certification) :");
    if (!key) return;
    const safe = key.trim().replace(/\s+/g, "_").toLowerCase();
    if (safe && !(safe in fields)) setField(safe, "");
  };

  const dropField = (k) => {
    const next = { ...fields };
    delete next[k];
    setFields(next);
  };

  const save = async (markConfirmed) => {
    setSaving(true);
    try {
      const attributes = {
        ...(doc.attributes || {}),
        extracted: fields,
      };
      const body = { attributes };
      if (markConfirmed) body.evidence_status = "confirmed";
      await api.patch(`/vault/documents/${doc.id}`, body);
      toast.success(markConfirmed
        ? "Champs verrouillés — statut passé à Confirmé."
        : "Champs sauvegardés.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sauvegarde impossible.");
    } finally { setSaving(false); }
  };

  const keys = Object.keys(fields);
  const confidence = doc?.extraction_confidence;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
         onClick={onClose}
         data-testid="vault-editor-backdrop">
      <div className="w-full max-w-2xl rounded-2xl bg-white border border-[#E4E1D8] shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}
           data-testid="vault-editor-modal">
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#E4E1D8]">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Correction des données extraites
            </div>
            <h3 className="mt-1 font-display text-lg truncate text-[#0B2545]">
              {doc.label}
            </h3>
            {confidence != null && (
              <div className="mt-0.5 flex items-center gap-1 text-[10px] font-mono text-[#5B5B62]">
                <Sparkles className="h-3 w-3" strokeWidth={2} />
                IA confiance {confidence}% · {doc.extracted_by || "—"}
              </div>
            )}
          </div>
          <button onClick={onClose}
                   data-testid="vault-editor-close"
                   className="text-[#5B5B62] hover:text-[#0B2545]">
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </header>

        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto space-y-2"
             data-testid="vault-editor-fields">
          {keys.length === 0 && (
            <div className="text-xs text-[#5B5B62] text-center py-6">
              Aucun champ extrait pour ce document. Lancez l'analyse IA d'abord.
            </div>
          )}
          {keys.map((k) => {
            const v = fields[k];
            const type = guessInputType(k, v);
            return (
              <div key={k} className="grid grid-cols-3 gap-2 items-start"
                   data-testid={`vault-editor-field-${k}`}>
                <label className="text-[11px] font-mono uppercase tracking-widest text-[#5B5B62] pt-2">
                  {humanizeKey(k)}
                </label>
                <div className="col-span-2 flex items-center gap-1">
                  {type === "checkbox" ? (
                    <input type="checkbox" checked={!!v}
                           onChange={(e) => setField(k, e.target.checked)}
                           className="h-4 w-4" />
                  ) : (
                    <input type={type} value={v ?? ""}
                           onChange={(e) => setField(k,
                              type === "number" && e.target.value !== ""
                                ? Number(e.target.value)
                                : e.target.value)}
                           className="flex-1 rounded border border-[#E4E1D8] px-2 py-1.5 text-xs" />
                  )}
                  <button onClick={() => dropField(k)}
                          className="text-[10px] font-mono text-[#B91C1C] hover:underline">
                    supprimer
                  </button>
                </div>
              </div>
            );
          })}
          <button onClick={addField}
                  data-testid="vault-editor-add-field"
                  className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] hover:text-[#0B2545]">
            + Ajouter un champ
          </button>
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E4E1D8] bg-[#F8F7F3]">
          <button onClick={() => save(false)} disabled={saving}
                   data-testid="vault-editor-save"
                   className="text-xs px-3 py-1.5 rounded border border-[#E4E1D8] text-[#5B5B62] hover:text-[#0B2545] hover:border-[#0B2545]">
            {saving ? <Loader2 className="h-3 w-3 animate-spin inline" strokeWidth={2} /> : "Sauvegarder"}
          </button>
          <button onClick={() => save(true)} disabled={saving}
                   data-testid="vault-editor-confirm"
                   className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[#059669] text-white hover:bg-[#047857] shadow">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> : <ShieldCheck className="h-3 w-3" strokeWidth={2} />}
            Confirmer & verrouiller
          </button>
        </footer>
      </div>
    </div>
  );
}
